import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  Tab,
  Tabs,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { Dropzone } from "../Dropzone/Dropzone";
import { WarningIcon } from "../Icons/Warning";
import styles from "./GalleryModal.module.scss";
import { Image } from "../../types/common";
import { FileWithPreview } from "../../types/common";
import { useTranslation } from "../../i18n";

export interface GalleryState {
  loading: boolean;
  images: Image[];
}

export interface EventInList {
  id: string;
  event_title: string;
}

export interface EventsContainingImage {
  list_of_events: EventInList[];
}

interface GalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  gallery: GalleryState;
  selectedImages: Image[];
  onImageSelect: (image: Image) => void;
  isImageSelected: (imageId: string | number) => boolean;
  onCheckImageInEvents: (image: Image) => Promise<void>;
  maxUploadingImages: number;
  onImageUpload: (files: FileWithPreview[]) => Promise<void>;
  onAssignImages: () => void;
  eventsContainingImage: EventsContainingImage | null;
  onImageDelete: (image: Image | null) => Promise<void>;
}

export const GalleryModal = ({
  isOpen,
  onClose,
  gallery,
  selectedImages,
  onImageSelect,
  isImageSelected,
  onCheckImageInEvents,
  maxUploadingImages,
  onImageUpload,
  onAssignImages,
  eventsContainingImage,
  onImageDelete,
}: GalleryModalProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [deleteImageModal, setDeleteImageModal] = useState<{
    isOpen: boolean;
    image: Image | null;
  }>({
    isOpen: false,
    image: null,
  });

  const canUploadImages =
    files?.length > 0 && files?.length <= maxUploadingImages;

  const handleClose = () => {
    setActiveTab(0);
    onClose();
  };

  const handleCheckImageInEvents = async (image: Image) => {
    await onCheckImageInEvents(image);
    setDeleteImageModal({ isOpen: true, image: image });
  };

  const handleDeleteImage = async (image: Image | null) => {
    await onImageDelete(image);
    setDeleteImageModal({ isOpen: false, image: null });
  };
  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="gallery-dialog"
        fullScreen
        style={{ margin: 16 }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          centered
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            paddingTop: 2,
            "& .MuiTab-root": {
              fontSize: "1.1rem",
              fontWeight: 600,
              textTransform: "none",
              minHeight: 64,
              padding: "16px 32px",
            },
            "& .Mui-selected": {
              color: "#8a2be2",
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#8a2be2",
              height: 3,
            },
          }}
        >
          <Tab label={t("gallery.tabSelect")} />
          <Tab label={t("gallery.tabOrg")} />
        </Tabs>
        <DialogContent className={styles.dialogContent}>
          {activeTab === 0 && (
            <>
              {gallery.loading ? (
                <div>
                  <CircularProgress />
                </div>
              ) : !gallery.images?.length ? (
                <div>
                  <small>
                    {t("gallery.empty")}
                  </small>
                </div>
              ) : (
                <>
                  <p style={{ marginBottom: 16, color: "#666" }}>
                    {t("gallery.selectHint")}
                  </p>
                  <div className={styles.imagesSection}>
                    {gallery.images?.map((image) => (
                      <div className={styles.imageWrapper} key={image.id}>
                        <img
                          src={image.url || (image as any).public_link || ""}
                          alt={t("gallery.imageAlt", { id: String(image.id) })}
                          width="120px"
                          height="120px"
                          className={classNames(styles.image, {
                            [styles.active]: isImageSelected(image.id || ""),
                          })}
                          onClick={() => onImageSelect(image)}
                        />
                      </div>
                    ))}
                    <div
                      className={styles.addImageTile}
                      onClick={() => setActiveTab(1)}
                    >
                      <AddIcon sx={{ fontSize: 48, color: "#8a2be2" }} />
                      <span>{t("gallery.uploadNew")}</span>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
          {activeTab === 1 && (
            <>
              {gallery.loading ? (
                <div>
                  <CircularProgress />
                </div>
              ) : (
                <>
                  <p style={{ marginBottom: 16, color: "#666" }}>
                    {t("gallery.uploadHint")}
                  </p>
                  <div className={styles.imagesSection}>
                    {gallery.images?.map((image) => (
                      <div className={styles.imageWrapper} key={image.id}>
                        <img
                          src={image.url || (image as any).public_link || ""}
                          alt={t("gallery.imageAlt", { id: String(image.id) })}
                          width="120px"
                          height="120px"
                          className={styles.image}
                        />
                        <IconButton
                          type="button"
                          aria-label={t("gallery.deleteAria")}
                          onClick={() => handleCheckImageInEvents(image)}
                          sx={{
                            position: "absolute",
                            top: 5,
                            right: 5,
                            zIndex: 1,
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                            "&:hover": {
                              color: "red",
                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                  <Divider style={{ margin: "24px 0" }}>
                    {t("gallery.uploadNew")}
                  </Divider>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <Button
                      variant="outlined"
                      onClick={async () => {
                        await onImageUpload(files);
                        setFiles([]);
                      }}
                      style={{ marginBottom: 16, marginLeft: "auto" }}
                      disabled={!canUploadImages}
                    >
                      {t("gallery.uploadToGallery")}
                    </Button>
                    <Dropzone
                      fullWidth
                      multiple
                      files={files}
                      setFiles={setFiles}
                      maxUploadingFiles={maxUploadingImages}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button variant="outlined" onClick={handleClose}>
            {t("common.close")}
          </Button>
          {activeTab === 0 && (
            <Button
              variant="outlined"
              onClick={() => {
                onAssignImages();
                handleClose();
              }}
              disabled={selectedImages.length === 0}
            >
              {t("gallery.assign", { count: selectedImages.length })}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteImageModal?.isOpen}
        onClose={() => setDeleteImageModal({ isOpen: false, image: null })}
        aria-labelledby="confirm-delete-dialog-title"
        aria-describedby="confirm-delete-dialog-description"
        maxWidth="xs"
      >
        <DialogTitle id="confirm-delete-dialog-title">
          <WarningIcon />
          {t("gallery.confirmDeleteTitle")}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <DialogContentText id="confirm-delete-dialog-description">
            {eventsContainingImage?.list_of_events &&
            eventsContainingImage.list_of_events.length > 0 ? (
              <>
                {t("gallery.inUse")}
                <List>
                  {eventsContainingImage.list_of_events.map((event) => (
                    <ListItem key={event.id} disablePadding>
                      <Link
                        to={`/organisation/event/${event.id}`}
                        target="_blank"
                      >
                        {event.event_title}
                      </Link>
                    </ListItem>
                  ))}
                </List>
              </>
            ) : (
              t("gallery.confirmDeleteText")
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button
            variant="outlined"
            color="inherit"
            fullWidth
            onClick={() => setDeleteImageModal({ isOpen: false, image: null })}
          >
            {t("gallery.cancelNo")}
          </Button>
          <Button
            variant="contained"
            fullWidth
            color="error"
            onClick={() => handleDeleteImage(deleteImageModal.image)}
            disabled={
              eventsContainingImage?.list_of_events &&
              eventsContainingImage.list_of_events.length > 0
            }
          >
            {t("gallery.confirmDeleteYes")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
